import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { GoogleMap } from '@react-google-maps/api';
import MapWrapper from './MapWrapper';
import CurrentLocationMarker from './CurrentLocationMarker';
import getUsingFallbackLocation from '../selectors/getUsingFallbackLocation';
import { logEvent } from '../utils/analytics';
import EVENTS from '../constants/events';
import getUserLocation from '../selectors/getUserLocation';
import combineStyles from '../utils/combineStyles';
import { updateForm } from '../slices/deliveryAddressForm';
import getThemeLookup from '../selectors/getThemeLookup';
import { CONTAINER_PROPERTIES, TEXT_PROPERTIES } from '../utils/theme';
import { useTranslation } from 'react-i18next';
import RedcatAdjustPinMoveMapIcon from './icons/redcat/RedcatAdjustPinMoveMapIcon';
import RedcatMapPinVariantIcon from './icons/redcat/RedcatMapPinVariantIcon';
import RedcatTargetIcon from './icons/redcat/RedcatTargetIcon';

const INITIAL_ZOOM = 18;

interface MapProps {
  mapHeight: number;
  markerPosition?: LocationCoordinates | null;
  zoomLevel?: number;
}

const Map = ({ mapHeight, markerPosition, zoomLevel = INITIAL_ZOOM }: MapProps) => {
  const dispatch = useAppDispatch();
  const p = useAppSelector(getThemeLookup);
  const { t } = useTranslation();
  const userCoords = useAppSelector(getUserLocation);
  const [tilesLoaded, setTilesLoaded] = useState(false);
  const usingFallbackLocation = useAppSelector(getUsingFallbackLocation);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const googleApiKey = useAppSelector(state => state.config.googleMapsApiKey);
  const formState = useAppSelector(state => state.deliveryAddressForm);

  const onLoad = React.useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const getInitialCenter = () => ({
    lat: markerPosition ? markerPosition.latitude : userCoords.latitude,
    lng: markerPosition ? markerPosition.longitude : userCoords.longitude,
  });

  const [center, setCenter] = useState(getInitialCenter());

  useEffect(() => {
    if (formState.resetMapCenter) {
      setCenter(getInitialCenter());
      dispatch(updateForm({ resetMapCenter: false }));
    }
  }, [formState.resetMapCenter, markerPosition, userCoords]);

  const onDragEnd = async () => {
    logEvent(EVENTS.PAN_MAP);

    if (!googleApiKey) {
      return;
    }

    const center = map?.getCenter();
    if (center) {
      const selectedLocation = {
        latitude: center.lat(),
        longitude: center.lng(),
      };

      dispatch(updateForm({ pinCoordinates: selectedLocation }));
    }
  };

  const onResetToCurrentLocation = async () => {
    setCenter({
      lat: userCoords.latitude,
      lng: userCoords.longitude,
    });
    if (map) {
      map.panTo({ lat: userCoords.latitude, lng: userCoords.longitude });
    }
    await onDragEnd();
  };

  const onZoomChanged = () => {
    onDragEnd();
    logEvent(EVENTS.CHANGE_MAP_ZOOM);
  };

  const onAdjustPinClicked = async () => {
    if (formState.adjustingPin) {
      return;
    }

    if (formState.page === 'DeliveryDetailsConfirmation') {
      dispatch(updateForm({ page: 'AddressLookupAndConfirmation', addressSelectionView: 'Map' }));
    }

    dispatch(updateForm({ adjustingPin: true }));

    await onDragEnd();
  };

  const getAdjustPinButtonText = () => {
    return formState.adjustingPin
      ? t('deliveryAddress.adjustPinActive')
      : t('deliveryAddress.adjustPin');
  };

  return (
    <div style={styles.container}>
      {!usingFallbackLocation && formState.adjustingPin && (
        <button
          style={combineStyles(
            styles.resetLocationButton,
            p('adjustPinButton', [...TEXT_PROPERTIES, ...CONTAINER_PROPERTIES]),
          )}
          onClick={onResetToCurrentLocation}
          type="button"
          aria-label={t('deliveryAddress.resetToCurrentLocation')}
        >
          <RedcatTargetIcon size={24} color={p('adjustPinButton', ['color']).color} />
        </button>
      )}

      <div style={styles.marker}>
        <RedcatMapPinVariantIcon size={32} color={p('adjustPinMarker', TEXT_PROPERTIES).color} />
      </div>
      <div style={styles.marker}>
        <RedcatMapPinVariantIcon size={32} color={p('adjustPinMarker', TEXT_PROPERTIES).color} />
      </div>
      <button
        style={combineStyles(
          styles.adjustPinButton,
          p('adjustPinButton', [...TEXT_PROPERTIES, ...CONTAINER_PROPERTIES]),
          formState.adjustingPin && { width: '75%', maxWidth: 300, cursor: 'default' },
        )}
        onClick={onAdjustPinClicked}
        type="button"
      >
        <div style={styles.adjustPinButtonInnerContainer}>
          {formState.adjustingPin && (
            <div style={{ marginRight: 8 }}>
              <RedcatAdjustPinMoveMapIcon size={32} color={p('adjustPinButton', ['color']).color} />
            </div>
          )}
          {getAdjustPinButtonText()}
        </div>
      </button>
      <MapWrapper>
        <GoogleMap
          onLoad={onLoad}
          onTilesLoaded={() => setTilesLoaded(true)}
          center={center}
          zoom={zoomLevel}
          options={{
            disableDefaultUI: true,
            gestureHandling: formState.adjustingPin ? 'greedy' : 'none',
            keyboardShortcuts: false,
            clickableIcons: false,
          }}
          onDragEnd={onDragEnd}
          onZoomChanged={onZoomChanged}
          mapContainerStyle={{ height: mapHeight, transition: 'height 0.3s ease' }}
        >
          {!usingFallbackLocation && tilesLoaded && <CurrentLocationMarker />}
        </GoogleMap>
      </MapWrapper>
    </div>
  );
};

const styles: Styles = {
  container: {
    position: 'relative',
  },
  marker: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, calc(-50% - 14px))',
    zIndex: 10,
    pointerEvents: 'none',
  },
  markerIcon: {
    transform: 'translate(0, -6px)',
  },
  adjustPinButtonInnerContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'left',
  },
  adjustPinButton: {
    position: 'absolute',
    left: '50%',
    bottom: 32,
    transform: 'translateX(-50%)',
    zIndex: 20,
    padding: '5px 24px',
    borderRadius: 32,
    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
  },
  adjustPinMoveMapIcon: {
    width: 48,
    height: 48,
    marginRight: 8,
  },
  resetLocationButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 30,
    borderRadius: '50%',
    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 6,
  },
};

export default React.memo(Map);
